import { describe, it, expect } from '@jest/globals';
import { getPackageSettings } from './getPackageSettings.js';

describe('getPackageSettings', () => {
  it('returns undefined if no package settings are defined', () => {
    const result = getPackageSettings({
      name: 'test',
      version: '1.0.0',
      userPackageSettings: [],
      generatedPackageSettings: [],
    });

    expect(result).toEqual({
      userPackageSettings: undefined,
      generatedPackageSettings: undefined,
    });
  });

  it('returns the package settings that match the package name and version', () => {
    const { userPackageSettings } = getPackageSettings({
      name: 'test',
      version: '1.2.3',
      userPackageSettings: [
        {
          match: { name: 'test', version: '^1.0.0' },
          exports: { './foo': './src/foo.js' },
        },
        {
          match: { name: 'test', version: '^2.0.0' },
          exports: { './bar': './src/bar.js' },
        },
      ],
      // this should do nothing since no built-in settings apply
      includeBuiltIn: true,
      generatedPackageSettings: undefined,
    });

    expect(userPackageSettings).toEqual({ exports: { './foo': './src/foo.js' } });
  });

  // NOTE: More cases for `match` string/object variants and wildcards are in checkMatch.test.ts

  it('merges multiple matching package settings, including patterns', () => {
    const { userPackageSettings } = getPackageSettings({
      name: 'test',
      version: '1.2.3',
      userPackageSettings: [
        {
          match: { name: 'test', version: '^1.0.0' },
          exports: { './foo': './src/foo.js' },
        },
        {
          match: { name: 'test', version: '^1.2.0' },
          exports: { './bar': './src/bar.js' },
        },
        {
          match: '*',
          exports: { './baz': './src/baz.js' },
        },
      ],
      generatedPackageSettings: undefined,
    });

    expect(userPackageSettings).toEqual({
      exports: [{ './foo': './src/foo.js' }, { './bar': './src/bar.js' }, { './baz': './src/baz.js' }],
    });
  });

  it('returns package settings and generated settings separately', () => {
    const result = getPackageSettings({
      name: 'test',
      version: '1.2.3',
      userPackageSettings: [
        {
          match: { name: 'test', version: '^1.0.0' },
          exports: { './foo': './src/foo.js' },
        },
      ],
      generatedPackageSettings: [
        {
          match: { name: 'test', version: '^1.0.0' },
          exports: { './bar': './src/bar.js' },
        },
      ],
    });

    expect(result).toEqual({
      userPackageSettings: {
        exports: { './foo': './src/foo.js' },
      },
      generatedPackageSettings: {
        exports: { './bar': './src/bar.js' },
      },
    });
  });

  it('returns a match for a prerelease package', () => {
    const { userPackageSettings } = getPackageSettings({
      name: 'test',
      version: '1.0.0-beta.1',
      userPackageSettings: [{ match: { name: 'test', version: '^1.0.0-beta.0' }, exports: {} }],
      generatedPackageSettings: undefined,
    });

    expect(userPackageSettings).toEqual({ exports: {} });
  });

  it('returns built-in settings if includeBuiltIn is true, even if no user settings', () => {
    const { userPackageSettings } = getPackageSettings({
      name: 'asap',
      version: '2.0.0',
      userPackageSettings: undefined,
      includeBuiltIn: true,
      generatedPackageSettings: undefined,
    });

    expect(userPackageSettings).toEqual({ bundler: 'ori' });
  });

  it('merges applicable built-in settings and user settings if includeBuiltIn is true', () => {
    const { userPackageSettings } = getPackageSettings({
      name: 'asap',
      version: '2.0.0',
      userPackageSettings: [
        {
          match: 'asap',
          ignoreMissingExports: true,
        },
      ],
      includeBuiltIn: true,
      generatedPackageSettings: undefined,
    });

    expect(userPackageSettings).toEqual({
      bundler: 'ori',
      ignoreMissingExports: true,
    });
  });
});
